import { Calendar as CalendarIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import type { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import type { AnalyticsCategory } from '../../config';
import { CATEGORY_IDS, type GranularityMode } from '../../constants';
import { useDateRange } from '../hooks/useDateRange';
import { MonthRangeControls } from './MonthRangeControls';
import { DayRangeControls } from './DayRangeControls';
import {
  clampDailyRange,
  getDefaultDailyRange,
  isWithinDailyLimit,
  toUtcEndOfDay,
  toUtcStartOfDay,
} from '../utils/dateRangeUtils';

interface DateRangePickerProps {
  readonly filters: BaseAnalyticsFilter;
  readonly onFiltersChange: (filters: BaseAnalyticsFilter) => void;
  readonly category?: AnalyticsCategory;
}

export default function DateRangePicker({
  filters,
  onFiltersChange,
  category,
}: DateRangePickerProps) {
  const isMobile = useIsMobile();
  const showGranularityToggle = category?.id === CATEGORY_IDS.SESSION_PERFORMANCE;
  const [mode, setMode] = useState<GranularityMode>(() => {
    if (!showGranularityToggle) {
      return 'month';
    }
    return filters.granularity || 'month';
  });

  const { getQuickRanges, isValidRange } = useDateRange();
  const quickRanges = useMemo(() => getQuickRanges(), [getQuickRanges]);

  const dispatchFilters = useCallback(
    (patch: Partial<BaseAnalyticsFilter>, nextMode?: GranularityMode) => {
      const merged: BaseAnalyticsFilter = {
        ...filters,
        ...patch,
        granularity: nextMode ?? patch.granularity ?? filters.granularity ?? 'month',
      };

      if (merged.granularity === 'day' && merged.startDate && merged.endDate) {
        if (!isWithinDailyLimit(merged.startDate, merged.endDate)) {
          const clamped = clampDailyRange({ startDate: merged.startDate, endDate: merged.endDate });
          onFiltersChange({ ...merged, ...clamped });
          return;
        }
      }

      onFiltersChange(merged);
    },
    [filters, onFiltersChange],
  );

  useEffect(() => {
    if (!showGranularityToggle && mode !== 'month') {
      setMode('month');
      dispatchFilters({ granularity: 'month' }, 'month');
    }
  }, [mode, showGranularityToggle, dispatchFilters]);

  useEffect(() => {
    if (filters.granularity && filters.granularity !== mode) {
      setMode(filters.granularity);
    }
  }, [filters.granularity, mode]);

  const handleModeChange = (nextMode: GranularityMode) => {
    if (nextMode === mode) {
      return;
    }

    if (nextMode === 'day') {
      const normalized =
        filters.startDate && filters.endDate
          ? {
              startDate: toUtcStartOfDay(filters.startDate),
              endDate: toUtcEndOfDay(filters.endDate),
            }
          : getDefaultDailyRange();

      dispatchFilters({ ...normalized }, 'day');
    } else {
      dispatchFilters({ granularity: 'month' }, 'month');
    }

    setMode(nextMode);
  };

  const handleMonthChange = (patch: Partial<BaseAnalyticsFilter>) => {
    dispatchFilters(patch, 'month');
  };

  const handleDayChange = (patch: Partial<BaseAnalyticsFilter>) => {
    dispatchFilters(patch, 'day');
  };

  const handleQuickMonthRange = (rangeKey: keyof ReturnType<typeof getQuickRanges>) => {
    const range = quickRanges[rangeKey];
    dispatchFilters({ startDate: range.startDate, endDate: range.endDate }, 'month');
  };

  const hasDateRange = Boolean(filters.startDate || filters.endDate);
  const isRangeValid = isValidRange(filters.startDate, filters.endDate);

  return (
    <div className={`flex items-center gap-x-3 gap-y-2 ${isMobile ? 'flex-col' : 'flex-row flex-wrap'}`}>
      {/* Calendar icon + optional granularity toggle + date picker — all inline */}
      <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${isMobile ? 'justify-center' : ''}`}>
        <CalendarIcon className="h-4 w-4 flex-shrink-0 text-slate-400" />

        {showGranularityToggle && (
          <div className="inline-flex overflow-hidden rounded-md border border-[var(--anl-border)]">
            <button
              className={`px-3 py-1.5 text-sm ${mode === 'month' ? 'bg-[var(--anl-accent)] text-white' : 'bg-[var(--anl-surface-elev)] text-[var(--anl-text)]'}`}
              onClick={() => handleModeChange('month')}
              aria-pressed={mode === 'month'}
            >
              Month
            </button>
            <button
              className={`border-l border-[var(--anl-border)] px-3 py-1.5 text-sm ${mode === 'day' ? 'bg-[var(--anl-accent)] text-white' : 'bg-[var(--anl-surface-elev)] text-[var(--anl-text)]'}`}
              onClick={() => handleModeChange('day')}
              aria-pressed={mode === 'day'}
            >
              Day
            </button>
          </div>
        )}

        {mode === 'month' ? (
          <MonthRangeControls filters={filters} onChange={handleMonthChange} />
        ) : (
          <DayRangeControls
            filters={filters}
            onChange={handleDayChange}
            showQuickPresets={showGranularityToggle}
          />
        )}
      </div>

      {/* Month quick-range preset buttons */}
      <div
        className={`relative z-10 flex items-center gap-x-1 ${showGranularityToggle && mode !== 'month' ? 'pointer-events-none invisible' : ''}`}
        aria-hidden={showGranularityToggle && mode !== 'month' ? true : undefined}
        inert={showGranularityToggle && mode !== 'month' ? true : undefined}
      >
          <button
            onClick={() => handleQuickMonthRange('lastThreeMonths')}
            className="cursor-pointer rounded border border-white/20 px-2 py-1.5 text-xs text-slate-200 transition-colors hover:bg-[var(--anl-surface-elev)]"
            style={{ backgroundColor: '#454b4d' }}
          >
            3M
          </button>
          <button
            onClick={() => handleQuickMonthRange('lastSixMonths')}
            className="cursor-pointer rounded border border-white/20 px-2 py-1.5 text-xs text-slate-200 transition-colors hover:bg-[var(--anl-surface-elev)]"
            style={{ backgroundColor: '#454b4d' }}
          >
            6M
          </button>
          <button
            onClick={() => handleQuickMonthRange('lastTwelveMonths')}
            className="cursor-pointer rounded border border-white/20 px-2 py-1.5 text-xs text-slate-200 transition-colors hover:bg-[var(--anl-surface-elev)]"
            style={{ backgroundColor: '#454b4d' }}
          >
            12M
          </button>
          <button
            onClick={() => handleQuickMonthRange('thisYear')}
            className="cursor-pointer rounded border border-white/20 px-2 py-1.5 text-xs text-slate-200 transition-colors hover:bg-[var(--anl-surface-elev)]"
            style={{ backgroundColor: '#454b4d' }}
          >
            YTD
          </button>
      </div>

      {hasDateRange && !isRangeValid && <div className="text-xs text-red-400">Invalid range</div>}
    </div>
  );
}
